/**
 * AI Features API Client
 * Handles AI-related functionality like voice transcription
 */

import { post, postFormData } from './apiClient';

interface CreateJobDescriptionResponse {
  description?: string;
  job_description?: string;
}

interface TranscribeAudioResponse {
  text: string;
}

interface GenerateJobDescriptionParams {
  job_title: string;
  department: string;
  user_requirements: string;
}

interface GenerateQuestionParams {
  job_title: string;
  department: string;
  job_description: string;
  question_category: string;
  existing_category_questions: string[];
}

interface GenerateQuestionResponse {
  question_category: string;
  suggested_question: string;
}

export const aiApi = {
  /**
   * Generate job description from audio transcription
   * @param audio_blob - The audio recording blob to transcribe
   * @returns A job description generated from the transcription
   */
  createJobDescription: async (audio_blob: Blob): Promise<CreateJobDescriptionResponse> => {
    // Create a FormData object to send the audio file
    const formData = new FormData();
    
    // CRITICAL: Create a proper File object from the Blob
    // The backend expects a file upload, not just a Blob
    const file = new File([audio_blob], 'recording.webm', { 
      type: audio_blob.type || 'audio/webm' 
    });
    
    // The backend expects a field named 'audio'
    formData.append('audio', file);
    
    // Log for debugging
    console.log('Sending audio file to create job description endpoint');
    console.log('Audio file type:', file.type);
    console.log('Audio file size:', file.size);
    console.log('Audio file name:', file.name);
    
    // Use our reusable postFormData function for file uploads
    return postFormData<CreateJobDescriptionResponse>('/ai/create-job-description', formData);
  },

  /**
   * Generate job description from text inputs
   * @param params - Job title, department, and requirements
   * @returns A job description generated from the inputs
   */
  generateJobDescription: async (params: GenerateJobDescriptionParams): Promise<CreateJobDescriptionResponse> => {
    return post<CreateJobDescriptionResponse>('/ai/create-job-description', params);
  },

  /**
   * Generate a question for a specific category using AI
   * @param params - Job details and existing questions
   * @returns The generated question response with category and suggested question
   */
  createQuestion: async (params: GenerateQuestionParams): Promise<GenerateQuestionResponse> => {
    return post<GenerateQuestionResponse>('/ai/create-question', params);
  },

  /**
   * Transcribe audio to text
   * @param audio_blob - The audio recording blob to transcribe
   * @returns The transcribed text
   */
  transcribeAudio: async (audio_blob: Blob): Promise<TranscribeAudioResponse> => {
    // Create a FormData object to send the audio file
    const formData = new FormData();
    
    // CRITICAL: Create a proper File object from the Blob
    // The backend expects a file upload, not just a Blob
    const file = new File([audio_blob], 'recording.mp3', { 
      type: audio_blob.type || 'audio/mp3' 
    });
    
    // The backend expects a field named 'audio' which maps to the UploadFile parameter
    formData.append('audio', file, 'recording.mp3');
    
    // Log for debugging
    console.log('Sending audio file to transcribe endpoint');
    console.log('Audio file type:', file.type);
    console.log('Audio file size:', file.size);
    console.log('Audio file name:', file.name);
    
    // Use our reusable postFormData function for file uploads
    return postFormData<TranscribeAudioResponse>('/ai/transcribe-audio', formData);
  }
};